/**
 * SHARED GEAR RULES — equipment limits, position affinity, and colouring.
 *
 * These rules were previously duplicated across `GearPage`, `GearItemCard` and
 * `PlayerAccordionTab`. The PlayerPage gear tab needs the same rules for its
 * Autoequip button and its card colouring, so rather than add a fourth copy
 * they are consolidated here.
 *
 * ⚠️ `EQUIPMENT_LIMITS` MIRRORS BACKEND ENFORCEMENT — it is not the authority.
 * `GearEquipService` validates every equip server-side and returns an error the
 * UI surfaces verbatim. These limits exist only so the UI can disable a control
 * and explain why BEFORE the user clicks. If this ever drifts from the backend,
 * the backend wins and the user sees its error; the failure mode is a wasted
 * round-trip, never an unauthorised write.
 */

/**
 * Maximum simultaneously-equipped items per slot, per player.
 *
 * ⚠️ `HANDS: 2` IS NOT REACHABLE THROUGH THE EQUIP ENDPOINT. See
 * `getDisplacedGear()` below: `GearEquipService.equip()` unequips EVERY
 * equipped item sharing a non-`ANY` candidate's slot, so a second HANDS item
 * replaces the first rather than joining it. The effective live limit for
 * every non-`ANY` slot is therefore 1.
 *
 * This table is deliberately NOT edited to say `HANDS: 1`. Whether the limit
 * or the displacement rule expresses the intended product behaviour is a
 * backend/product decision, and quietly changing the number here would bake a
 * frontend guess into shared rules. Instead the UI now DISCLOSES displacement
 * before the user commits — truthful either way, and correct without needing
 * that decision made first.
 */
export const EQUIPMENT_LIMITS = {
    HEAD: 1,
    CHEST: 1,
    HANDS: 2,
    LEGS: 1,
    FEET: 1,
    ANY: 3,
};

/** Hard cap on total equipped items per player, independent of slot. */
export const MAX_EQUIPPED_PER_PLAYER = 3;

/** Position-based card colours, matching the draft system and GearItemCard. */
export const GEAR_POSITION_COLORS = {
    QB: '#f1c40f',    // Yellow
    RB: '#3498db',    // Blue
    WR: '#e74c3c',    // Red
    QB_RB: '#27ae60', // Green  (hybrid QB/RB)
    RB_WR: '#9b59b6', // Purple (hybrid RB/WR)
    NONE: '#ffffff',  // White  (no position signal)
};

/**
 * Infer which positions a gear item benefits by reading its player-facing text.
 *
 * ⚠️ THIS IS A HEURISTIC OVER DESCRIPTION TEXT, NOT A DECLARED FIELD. Gear has
 * no position column; the existing `GearItemCard` colouring has always derived
 * affinity this way and this function is a faithful extraction of that logic so
 * the two surfaces cannot disagree. It is used for COSMETIC colouring and for
 * ORDERING Autoequip suggestions — never to block an equip. A user can still
 * equip any item on any player; a wrong guess costs a suboptimal suggestion,
 * not a lost action.
 */
export function analyzeGearPosition(description) {
    if (!description) return { isQB: false, isRB: false, isWR: false };

    const text = description.toLowerCase();

    return {
        isQB: /pass|completion|interception/i.test(text),
        isRB: /rush|carr/i.test(text),
        isWR: /reception|receiving|target|\brec\b/i.test(text),
    };
}

/** Background colour for a gear card, from its inferred position affinity. */
export function getGearPositionColor(gear) {
    const description = gear?.player_description || gear?.description || '';
    const { isQB, isRB, isWR } = analyzeGearPosition(description);

    if ((isQB && isRB) || (isQB && isWR)) return GEAR_POSITION_COLORS.QB_RB;
    if (isRB && isWR) return GEAR_POSITION_COLORS.RB_WR;
    if (isQB) return GEAR_POSITION_COLORS.QB;
    if (isRB) return GEAR_POSITION_COLORS.RB;
    if (isWR) return GEAR_POSITION_COLORS.WR;

    return GEAR_POSITION_COLORS.NONE;
}

/**
 * Does this gear item's text suggest it benefits the given position?
 */
export function isGearAlignedWithPosition(gear, position) {
    if (!position) return false;

    const description = gear?.player_description || gear?.description || '';
    const { isQB, isRB, isWR } = analyzeGearPosition(description);

    if (position === 'QB') return isQB;
    if (position === 'RB') return isRB;
    if (position === 'WR') return isWR;

    return false;
}

/**
 * Count a player's currently-equipped items, bucketed by slot.
 */
export function getEquippedCountsBySlot(teamGear, characterSheetId) {
    const counts = {};
    Object.keys(EQUIPMENT_LIMITS).forEach((slot) => {
        counts[slot] = 0;
    });

    (teamGear || [])
        .filter((item) => item.character_sheet === characterSheetId)
        .forEach((item) => {
            if (counts[item.slot] !== undefined) counts[item.slot] += 1;
        });

    return counts;
}

/**
 * Why a player cannot accept an item — or `null` when they can.
 *
 * THE SINGLE ELIGIBILITY RULE. Every surface that offers an equip target must
 * go through this (or `canPlayerAcceptGear()` below), because an item can be
 * refused for THREE independent reasons and checking only one silently offers
 * impossible actions:
 *
 *   • `'NO_CHARACTER_SHEET'` — we do not know this player's CharacterSheet PK,
 *     so we cannot name an equip target at all. See the guard below; this is a
 *     malformed-payload state, not a capacity state.
 *   • `'ROSTER_FULL'` — already holding `MAX_EQUIPPED_PER_PLAYER` items, in any
 *     combination of slots. This is the check `GearItemCard` was missing: its
 *     local `canPlayerEquipSlot()` compared only the candidate's own slot, so a
 *     player holding three items in OTHER slots still got an enabled button and
 *     the request died at the server.
 *   • `'SLOT_FULL'` — at the per-slot limit (e.g. a second LEGS item).
 *
 * Order matters for the message, not the outcome: identity is checked before
 * capacity (a count against an unknown player is meaningless), then the roster
 * cap ahead of the slot limit because it is the broader, less obvious one.
 *
 * @returns {'NO_CHARACTER_SHEET'|'ROSTER_FULL'|'SLOT_FULL'|null}
 */
export function getGearRejectionReason(teamGear, characterSheetId, slot) {
    // ⚠️ IDENTITY BEFORE CAPACITY — DO NOT MOVE BELOW THE COUNTING.
    //
    // A missing sheet id is not "a player with an empty loadout", which is what
    // the counting below would conclude. `character_sheet` is null on every
    // UNEQUIPPED row, so `item.character_sheet === characterSheetId` with a
    // null id matches the entire unheld pool: a team with three or more unheld
    // items would read as ROSTER_FULL, and a team with fewer would read as
    // freely equippable. Both are fabrications about a player we cannot
    // identify, and the second one hands the caller an enabled control whose
    // target is unknown.
    if (characterSheetId === undefined || characterSheetId === null) {
        return 'NO_CHARACTER_SHEET';
    }

    const playerGear = (teamGear || []).filter(
        (item) => item.character_sheet === characterSheetId
    );

    if (playerGear.length >= MAX_EQUIPPED_PER_PLAYER) return 'ROSTER_FULL';

    const slotCount = playerGear.filter((item) => item.slot === slot).length;
    const slotLimit = EQUIPMENT_LIMITS[slot] ?? 1;
    if (slotCount >= slotLimit) return 'SLOT_FULL';

    return null;
}

/**
 * Can this player accept one more item in this slot right now?
 *
 * Checks BOTH the per-slot limit and the overall 3-item cap, because an item
 * can be rejected by either.
 *
 * ⚠️ MIRRORS BACKEND ENFORCEMENT, DOES NOT REPLACE IT. `GearEquipService`
 * validates every equip server-side; this exists so the UI can disable a
 * control and explain why before the user clicks.
 */
export function canPlayerAcceptGear(teamGear, characterSheetId, slot) {
    return getGearRejectionReason(teamGear, characterSheetId, slot) === null;
}

/**
 * WHICH OF THIS PLAYER'S ITEMS WOULD THIS EQUIP SILENTLY REMOVE?
 *
 * ============================================================================
 * THE UI PROMISED CAPACITY THE ENDPOINT DOES NOT PROVIDE. READ BEFORE EDITING.
 * ============================================================================
 * `GearEquipService.equip()` applies DISPLACEMENT-BY-SLOT: for any candidate
 * whose slot is not `ANY`, it unequips EVERY equipped item already in that slot
 * (`backend/football/services/gear_equip.py` — `if persisted_gear.slot != 'ANY'`,
 * then each displaced row is unequipped individually).
 *
 * That collides with `EQUIPMENT_LIMITS.HANDS = 2`. A player holding one HANDS
 * item is under the limit, so the UI enabled the button and badged it
 * "1/2 HANDS" — a direct promise that a second would join the first. The equip
 * then displaced the first, and the player ended with ONE hand item. The user
 * was told capacity, and got replacement.
 *
 * ⚠️ THIS IS REACHABLE THROUGH ORDINARY SINGLE-ITEM EQUIP. An earlier note in
 * this file asserted single-item equips were "unaffected" because the removed
 * multi-item planner was the obvious victim. The planner made it worse, not
 * possible: one click on one card is enough to lose an item the badge said
 * there was room for.
 *
 * WHY THIS DISCLOSES RATHER THAN BLOCKS. The displacement may well be the
 * intended product behaviour — replacing a worn item is a reasonable model.
 * What is not defensible is doing it silently while the badge says otherwise.
 * Returning the affected rows lets the caller say "this replaces X" up front,
 * which is honest under either resolution of the limit-vs-displacement
 * question and needs no product decision to ship.
 *
 * `ANY` items never displace — the backend exempts them explicitly — so they
 * return an empty list and stack up to the 3-item roster cap as expected.
 *
 * @returns {Array<Object>} the equipped rows this equip would remove; empty
 *          when nothing is displaced.
 */
export function getDisplacedGear(teamGear, characterSheetId, slot) {
    // Without an identified sheet there is no loadout to reason about. The
    // caller is already blocked by `NO_CHARACTER_SHEET`; returning [] here
    // avoids the same null-matches-unequipped-rows trap documented above.
    if (characterSheetId === undefined || characterSheetId === null) return [];

    // The backend exempts ANY from displacement entirely.
    if (slot === 'ANY') return [];

    return (teamGear || []).filter(
        (item) => item.character_sheet === characterSheetId && item.slot === slot
    );
}

/*
 * ============================================================================
 * ⚠️ NO MULTI-ITEM PLANNING HELPER LIVES HERE — AND ONE MUST NOT BE ADDED
 *    WITHOUT RESOLVING THE DIVERGENCE BELOW.
 * ============================================================================
 * `selectAutoequipGear()` was removed on 2026-09-27 along with the Autoequip /
 * Clear buttons it served. It simulated capacity item-by-item against
 * `EQUIPMENT_LIMITS`, which cannot predict the real outcome of a multi-item
 * equip:
 *
 *   `GearEquipService.equip()` applies DISPLACEMENT-BY-SLOT to every non-ANY
 *   item — it unequips EVERY existing item sharing the candidate's slot
 *   (backend/football/services/gear_equip.py, `if persisted_gear.slot != 'ANY'`).
 *
 * So `EQUIPMENT_LIMITS.HANDS = 2` describes a state the equip endpoint cannot
 * reach: equipping a second HANDS item displaces the first. Any planner that
 * trusts the limit will select two hand items, execute both, and leave the user
 * with one — silently, and differently from what the UI promised.
 *
 * ⚠️ THIS DIVERGENCE IS PRE-EXISTING AND NOT CONFINED TO THIS FEATURE.
 * `EQUIPMENT_LIMITS` is also what `GearItemCard`'s "1/2 HANDS" badge and its
 * slot-eligibility check display on GearPage. Whether the per-slot limit or the
 * displacement rule is the intended product behaviour is a backend/product
 * decision, so the NUMBER has been REPORTED rather than patched from the
 * frontend.
 *
 * ⚠️ CORRECTION (2026-09-27): AN EARLIER VERSION OF THIS NOTE CLAIMED
 * "SINGLE-ITEM equip/unequip is unaffected and remains correct". THAT IS
 * WRONG, and the error mattered — it is why the badge kept promising capacity
 * it could not deliver long after the planner that "caused" it was deleted.
 *
 * One click on one card is enough: a player holding one HANDS item is under
 * `HANDS: 2`, so the card enables and badges "1/2 HANDS", and the equip then
 * DISPLACES the existing item. The refetch afterwards does show the truth, but
 * only after the item is gone — the user was told there was room, acted on it,
 * and silently lost gear. Multi-item planning made this worse; it did not make
 * it possible.
 *
 * `getDisplacedGear()` above now lets any caller state that consequence BEFORE
 * the click, which is honest regardless of how the limit-vs-displacement
 * question is eventually resolved.
 */
